import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, Outlet, NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { LogOut, LayoutDashboard, Package, Building2, Newspaper, FileText, MessageSquare, Settings as SettingsIcon, Trash2, Edit3, Plus, Save, X, Factory, Award, ChevronUp, ChevronDown, Eye, EyeOff, UserCog, ShieldCheck } from "lucide-react";
import FileField from "@/components/admin/FileField";

const ADMIN_MODULES = ["products", "projects", "branches", "clients", "news", "documents", "enquiries", "settings"];

function PasswordField({ value, onChange, placeholder, testid, required = false }) {
  const [visible, setVisible] = useState(false);
  return <div className="relative">
    <input data-testid={testid} required={required} type={visible ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="w-full border border-black/10 px-4 py-3 pr-11 text-sm focus:outline-none focus:border-pipeco-green"/>
    <button type="button" data-testid={`${testid}-visibility-toggle`} onClick={()=>setVisible(!visible)} className="absolute right-0 inset-y-0 px-3 text-slate-500" aria-label={visible ? "Hide password" : "Show password"}>{visible ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
  </div>;
}

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { login, user } = useAuth();
  const nav = useNavigate();
  if (user) return <Navigate to="/admin" replace/>;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); nav("/admin"); }
    catch (e) { setErr(String(e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-pipeco-dark text-white grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white text-slate-900 p-10 shadow-2xl">
        <div className="font-display text-3xl font-black text-pipeco-green"><img src="/pipeco-logo.png" alt="PIPECO GROUP" className="h-12 w-auto"/></div>
        <div className="overline text-pipeco-green mt-3">Admin · Staff Login</div>
        <h1 className="headline text-3xl mt-4">Sign In</h1>
        <div className="mt-6 space-y-3">
          <input data-testid="admin-email" required type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" className="w-full border border-black/10 px-4 py-3 text-sm focus:outline-none focus:border-pipeco-green"/>
          <PasswordField testid="admin-password" required value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password"/>
        </div>
        {err && <div data-testid="admin-login-error" className="text-red-600 text-sm mt-3">{err}</div>}
        <button data-testid="admin-login-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center mt-5">{loading?"Signing in…":"Sign In"}</button>
        <button type="button" data-testid="forgot-password-button" onClick={()=>setForgot(!forgot)} className="text-xs mt-4 text-pipeco-green hover-line">Forgot Password?</button>
        {forgot && <div data-testid="forgot-password-message" className="mt-3 text-xs leading-relaxed text-slate-600">Forgot your password? Please contact the system administrator at <a href="mailto:admin@pipecogroup.com" className="text-pipeco-green underline">admin@pipecogroup.com</a>.</div>}
        <Link to="/" className="text-xs mt-6 block text-center text-slate-500 hover-line">← Back to site</Link>
      </form>
    </div>
  );
}

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace/>;
  const canAccess = (permission) => user.role === "super_admin" || user.permissions?.includes(permission);
  const items = [
    ["/admin", LayoutDashboard, "Dashboard", null],
    ["/admin/products", Package, "Products", "products"],
    ["/admin/projects", Factory, "Projects", "projects"],
    ["/admin/branches", Building2, "Branches", "branches"],
    ["/admin/clients", Award, "Clients", "clients"],
    ["/admin/news", Newspaper, "News", "news"],
    ["/admin/documents", FileText, "Documents", "documents"],
    ["/admin/enquiries", MessageSquare, "Enquiries", "enquiries"],
    ["/admin/settings", SettingsIcon, "Settings", "settings"],
    ["/admin/legal", FileText, "Legal Pages", "legal"],
    ["/admin/account", ShieldCheck, "Account", null],
    ["/admin/users", UserCog, "Admin Users", "users"],
  ];
  const activeItem = items.find(([to]) => to !== "/admin" && location.pathname.startsWith(to));
  if (activeItem && ((activeItem[3] === "users" && user.role !== "super_admin") || (activeItem[3] && activeItem[3] !== "users" && !canAccess(activeItem[3])))) return <Navigate to="/admin" replace/>;
  return (
    <div data-testid="admin-layout" className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside className="w-64 bg-pipeco-dark text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <img src="/pipeco-logo.png" alt="PIPECO" className="h-8 w-auto brightness-0 invert"/>
          <div className="overline text-white/50 text-[9px] mt-2">Admin Panel</div>
        </div>
        <nav className="flex-1 py-4">
          {items.filter(([, , , permission]) => !permission || (permission === "users" ? user.role === "super_admin" : canAccess(permission))).map(([to, Ic, label]) => (
            <NavLink key={to} to={to} end={to==="/admin"} data-testid={`admin-nav-${label.toLowerCase().replace(" ", "-")}`} className={({isActive})=>`flex items-center gap-3 px-6 py-3 text-sm ${isActive?"bg-white/10 border-l-2 border-emerald-300":"text-white/70 hover:text-white hover:bg-white/5"}`}>
              <Ic size={16}/> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/70">{user.email}</div>
          <button onClick={logout} data-testid="admin-logout" className="mt-2 flex items-center gap-2 text-xs text-white/60 hover:text-white"><LogOut size={12}/>Logout</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-auto"><Outlet/></main>
    </div>
  );
}

export function AdminDashboard() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    Promise.all(["products","projects","branches","news","documents"].map(k => api.get(`/${k}`).then(r=>[k, r.data.length]).catch(()=>[k,0])))
      .then(entries => setCounts(Object.fromEntries(entries)));
    api.get("/enquiries").then(r => setCounts(c => ({ ...c, enquiries: r.data.length }))).catch(()=>{});
  }, []);
  const cards = [["Products","products"],["Projects","projects"],["Branches","branches"],["News","news"],["Documents","documents"],["Enquiries","enquiries"]];
  return (
    <div className="p-10">
      <div className="overline text-pipeco-green">Admin</div>
      <h1 className="headline text-4xl mt-2">Dashboard</h1>
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {cards.map(([label, k]) => (
          <div key={k} data-testid={`dashboard-${k}`} className="p-6 bg-white border border-black/5">
            <div className="overline text-slate-500">{label}</div>
            <div className="font-display text-5xl font-black text-pipeco-green mt-2">{counts[k] ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------ Generic CRUD Manager ------------------ */
function orderAdminItems(resource, items) {
  return items;
}

function isHeadOffice(item) {
  const name = (item.name || "").toLowerCase();
  return name.includes("head office") || (item.branch_type || "").toLowerCase() === "head office" || name === "pipeco group";
}

function CrudManager({ title, resource, fields, testid }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | object
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/${resource}`).then(r => setItems(orderAdminItems(resource, r.data)));
  useEffect(() => { load(); }, [resource]);

  const openNew = () => { setForm(Object.fromEntries(fields.map(f => [f.key, f.default ?? ""]))); setEditing("new"); };
  const openEdit = (it) => { setForm({...it}); setEditing(it); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {...form};
      // convert comma-separated arrays back
      fields.forEach(f => { if (f.array && typeof payload[f.key] === "string") payload[f.key] = payload[f.key].split(",").map(s=>s.trim()).filter(Boolean); });
      if (editing === "new") await api.post(`/${resource}`, payload);
      else await api.put(`/${resource}/${editing.id}`, payload);
      setEditing(null); await load();
    } catch (e) { alert(String(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/${resource}/${id}`); load();
  };

  const moveBranch = async (index, direction) => {
    const targetIndex = index + direction;
    if (resource !== "branches" || targetIndex < 0 || targetIndex >= items.length || isHeadOffice(items[index]) || isHeadOffice(items[targetIndex])) return;
    const reordered = [...items];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setBusy(true);
    try {
      await api.post("/branches/reorder", { ordered_ids: reordered.map(item => item.id) });
      await load();
    } catch (e) { alert(String(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const moveDocument = async (index, direction) => {
    const targetIndex = index + direction;
    if (resource !== "documents" || targetIndex < 0 || targetIndex >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setBusy(true);
    try {
      await api.post("/documents/reorder", { ordered_ids: reordered.map(item => item.id) });
      await load();
    } catch (e) { alert(String(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-10" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-pipeco-green">{resource}</div>
          <h1 className="headline text-4xl mt-2">{title}</h1>
        </div>
        <button data-testid={`${testid}-new`} onClick={openNew} className="btn-primary"><Plus size={14}/>New</button>
      </div>
      <div className="bg-white border border-black/5">
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>{fields.filter(f=>f.list).map(f=><th key={f.key} className="text-left px-4 py-3 uppercase text-[10px] tracking-widest text-slate-500">{f.label}</th>)}<th className="w-32"></th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-black/5">
                {fields.filter(f=>f.list).map(f => <td key={f.key} className="px-4 py-3">{String(it[f.key] ?? "").slice(0,50)}</td>)}
                <td className="px-4 py-2 text-right">
                  {resource === "branches" && <>
                    <button onClick={()=>moveBranch(items.indexOf(it), -1)} disabled={busy || isHeadOffice(it) || items.indexOf(it) === 0 || isHeadOffice(items[items.indexOf(it)-1])} title="Move branch up" className="p-2 hover:bg-slate-100 disabled:opacity-30" data-testid={`${testid}-move-up-${it.id}`}><ChevronUp size={14}/></button>
                    <button onClick={()=>moveBranch(items.indexOf(it), 1)} disabled={busy || isHeadOffice(it) || items.indexOf(it) === items.length-1} title="Move branch down" className="p-2 hover:bg-slate-100 disabled:opacity-30" data-testid={`${testid}-move-down-${it.id}`}><ChevronDown size={14}/></button>
                  </>}
                  {resource === "documents" && <>
                    <button onClick={()=>moveDocument(items.indexOf(it), -1)} disabled={busy || items.indexOf(it) === 0} title="Move document up" className="p-2 hover:bg-slate-100 disabled:opacity-30" data-testid={`${testid}-move-up-${it.id}`}><ChevronUp size={14}/></button>
                    <button onClick={()=>moveDocument(items.indexOf(it), 1)} disabled={busy || items.indexOf(it) === items.length-1} title="Move document down" className="p-2 hover:bg-slate-100 disabled:opacity-30" data-testid={`${testid}-move-down-${it.id}`}><ChevronDown size={14}/></button>
                  </>}
                  <button onClick={()=>openEdit(it)} className="p-2 hover:bg-slate-100" data-testid={`${testid}-edit-${it.id}`}><Edit3 size={14}/></button>
                  <button onClick={()=>remove(it.id)} className="p-2 hover:bg-slate-100 text-red-600" data-testid={`${testid}-delete-${it.id}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
            {items.length===0 && <tr><td colSpan={99} className="px-4 py-8 text-center text-slate-500">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={()=>setEditing(null)}>
          <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b">
              <div className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} · {title}</div>
              <button onClick={()=>setEditing(null)} className="p-2"><X size={20}/></button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-4">
              {fields.map(f => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{f.label}</label>
                  {f.type === "file" ? (
                    <div className="space-y-2">
                      <FileField
                        value={form[f.key] ?? (f.gallery ? [] : "")}
                        onChange={(v)=>setForm({...form,[f.key]:v})}
                        accept={f.accept || "image"}
                        gallery={!!f.gallery}
                        fit={f.fit || "cover"}
                      />
                      <input
                        value={Array.isArray(form[f.key]) ? "" : (form[f.key] ?? "")}
                        onChange={e=>setForm({...form,[f.key]: f.gallery ? (e.target.value ? [e.target.value] : []) : e.target.value})}
                        placeholder={f.gallery ? "…or paste single URL (uploads preferred)" : "…or paste URL"}
                        className="w-full border border-black/10 px-3 py-2 text-xs bg-slate-50"
                      />
                    </div>
                  ) : f.type === "textarea" ? (
                    <textarea rows={4} value={Array.isArray(form[f.key])?form[f.key].join(", "):(form[f.key]??"")} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="w-full border border-black/10 px-3 py-2 text-sm"/>
                  ) : f.type === "select" ? (
                    <select value={form[f.key]??""} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="w-full border border-black/10 px-3 py-2 text-sm">
                      {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "boolean" ? (
                    <select value={form[f.key]?"true":"false"} onChange={e=>setForm({...form,[f.key]:e.target.value==="true"})} className="w-full border border-black/10 px-3 py-2 text-sm">
                      <option value="false">No</option><option value="true">Yes</option>
                    </select>
                  ) : (
                    <input value={Array.isArray(form[f.key])?form[f.key].join(", "):(form[f.key]??"")} onChange={e=>setForm({...form,[f.key]:e.target.value})} className="w-full border border-black/10 px-3 py-2 text-sm"/>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 p-6 border-t">
              <button onClick={()=>setEditing(null)} className="btn-outline-dark">Cancel</button>
              <button data-testid={`${testid}-save`} onClick={save} disabled={busy} className="btn-primary"><Save size={14}/>{busy?"Saving…":"Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminProducts() {
  return <CrudManager title="Products" resource="products" testid="admin-products" fields={[
    {key:"sort_order", label:"Display Order (1=first)", list:true},
    {key:"slug", label:"Slug", list:true},
    {key:"name", label:"Name", list:true},
    {key:"tank_code", label:"Tank Code (e.g. GRP-S)"},
    {key:"short_name", label:"Short Name"},
    {key:"intro", label:"Intro", type:"textarea", full:true},
    {key:"main_image", label:"Main Image", type:"file", accept:"image", fit:"cover", full:true},
    {key:"gallery", label:"Gallery", type:"file", accept:"image", gallery:true, fit:"cover", full:true},
    {key:"features", label:"Features (comma)", array:true, full:true, type:"textarea"},
    {key:"applications", label:"Applications (comma)", array:true, full:true},
    {key:"chips", label:"Highlight Chips (comma, max 3)", array:true, full:true},
    {key:"materials", label:"Materials"},
    {key:"configurations", label:"Configurations"},
    {key:"capacity_info", label:"Capacity Info", full:true},
    {key:"technical_info", label:"Technical Info", type:"textarea", full:true},
    {key:"accessories", label:"Accessories (comma)", array:true, full:true},
    {key:"installation", label:"Installation", type:"textarea", full:true},
    {key:"quality", label:"Quality"},
    {key:"warranty", label:"Warranty"},
    {key:"catalogue_url", label:"Catalogue PDF", type:"file", accept:"pdf", full:true},
    {key:"featured", label:"Featured", type:"boolean", list:true},
  ]}/>;
}

export function AdminClients() {
  return <CrudManager title="Clients / Completed Projects" resource="clients" testid="admin-clients" fields={[
    {key:"sort_order", label:"Display Order", list:true},
    {key:"name", label:"Client Name", list:true},
    {key:"country", label:"Country", list:true},
    {key:"logo", label:"Logo Image", type:"file", accept:"image", fit:"contain", full:true},
    {key:"website", label:"Website"},
    {key:"featured", label:"Featured on Home", type:"boolean", list:true},
  ]}/>;
}

export function AdminProjects() {
  return <CrudManager title="Projects" resource="projects" testid="admin-projects" fields={[
    {key:"name", label:"Project Name", list:true},
    {key:"country", label:"Country", list:true},
    {key:"city", label:"City"},
    {key:"industry", label:"Industry", list:true},
    {key:"tank_type", label:"Tank Type"},
    {key:"capacity", label:"Capacity"},
    {key:"number_of_tanks", label:"No. of Tanks"},
    {key:"products_supplied", label:"Products Supplied"},
    {key:"scope", label:"Scope of Supply", type:"textarea", full:true},
    {key:"installation_details", label:"Installation Details", type:"textarea", full:true},
    {key:"description", label:"Description", type:"textarea", full:true},
    {key:"status", label:"Status", type:"select", options:["Ongoing","Completed","Supplied","Installed","Commissioned"], list:true},
    {key:"completion_date", label:"Completion Date"},
    {key:"main_image", label:"Main Image", type:"file", accept:"image", fit:"cover", full:true},
    {key:"gallery", label:"Gallery", type:"file", accept:"image", gallery:true, fit:"cover", full:true},
    {key:"featured", label:"Featured", type:"boolean"},
    {key:"published", label:"Published", type:"boolean", list:true},
  ]}/>;
}

export function AdminBranches() {
  return <CrudManager title="Branches" resource="branches" testid="admin-branches" fields={[
    {key:"name", label:"Office Name", list:true},
    {key:"country", label:"Country", list:true},
    {key:"country_code", label:"Country Code (ISO2)"},
    {key:"city", label:"City", list:true},
    {key:"branch_type", label:"Branch Type"},
    {key:"address", label:"Address", type:"textarea", full:true},
    {key:"phone", label:"Phone"},
    {key:"whatsapp", label:"WhatsApp"},
    {key:"email", label:"Email"},
    {key:"website", label:"Website"},
    {key:"contact_person", label:"Contact Person"},
    {key:"working_hours", label:"Working Hours"},
    {key:"maps_link", label:"Google Maps Link"},
    {key:"lat", label:"Latitude"},
    {key:"lng", label:"Longitude"},
    {key:"main_image", label:"Office Photo", type:"file", accept:"image", fit:"cover", full:true},
    {key:"description", label:"Description", type:"textarea", full:true},
    {key:"featured", label:"Featured", type:"boolean"},
  ]}/>;
}

export function AdminNews() {
  return <CrudManager title="News" resource="news" testid="admin-news" fields={[
    {key:"title", label:"Title", list:true},
    {key:"category", label:"Category", type:"select", options:["Company News","Project Updates","Product Updates","Branch News","Events","Exhibitions","Announcements","Technical Articles"], list:true},
    {key:"author", label:"Author"},
    {key:"content", label:"Content", type:"textarea", full:true},
    {key:"featured_image", label:"Featured Image", type:"file", accept:"image", fit:"cover", full:true},
    {key:"date", label:"Date"},
    {key:"seo_title", label:"SEO Title", full:true},
    {key:"meta_description", label:"Meta Description", type:"textarea", full:true},
    {key:"published", label:"Published", type:"boolean", list:true},
  ]}/>;
}

export function AdminDocuments() {
  return <CrudManager title="Documents" resource="documents" testid="admin-documents" fields={[
    {key:"title", label:"Title", list:true},
    {key:"category", label:"Category", type:"select", options:["Company Profile","Individual Company Profile","Catalogues","Brochures","Technical Datasheets","Installation Manuals","Warranty Documents","Certifications","Project References","Presentations"], list:true},
    {key:"description", label:"Description", type:"textarea", full:true},
    {key:"cover_image", label:"Cover Image", type:"file", accept:"image", fit:"cover", full:true},
    {key:"pdf_url", label:"PDF File", type:"file", accept:"pdf", full:true},
    {key:"language", label:"Language"},
    {key:"version", label:"Version"},
    {key:"featured", label:"Featured", type:"boolean"},
    {key:"published", label:"Published", type:"boolean", list:true},
  ]}/>;
}

export function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/enquiries").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if(!window.confirm("Delete?")) return; await api.delete(`/enquiries/${id}`); load(); };
  return (
    <div className="p-10" data-testid="admin-enquiries">
      <div className="overline text-pipeco-green">Admin</div>
      <h1 className="headline text-4xl mt-2">Enquiries</h1>
      <div className="mt-6 bg-white border border-black/5">
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>{["Name","Email","Country","Product","Received",""].map(h=><th key={h} className="text-left px-4 py-3 uppercase text-[10px] tracking-widest text-slate-500">{h}</th>)}</tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-black/5 hover:bg-slate-50 cursor-pointer" onClick={()=>setSel(it)} data-testid={`enquiry-row-${it.id}`}>
                <td className="px-4 py-3 font-medium">{it.name}</td><td className="px-4 py-3">{it.email}</td><td className="px-4 py-3">{it.country}</td><td className="px-4 py-3">{it.product}</td><td className="px-4 py-3 text-xs text-slate-500">{new Date(it.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-right"><button onClick={(e)=>{e.stopPropagation(); remove(it.id);}} className="p-2 text-red-600"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {items.length===0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No enquiries yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={()=>setSel(null)}>
          <div className="bg-white w-full max-w-2xl p-8" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><div className="font-display text-2xl">{sel.name}</div><button onClick={()=>setSel(null)}><X size={20}/></button></div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              {["email","phone","whatsapp","company","country","product","capacity","project_type","project_location"].map(k=>(
                <div key={k}><dt className="text-xs uppercase tracking-widest text-slate-500">{k}</dt><dd>{sel[k]||"—"}</dd></div>
              ))}
            </dl>
            <div className="mt-6"><div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Message</div><p className="text-sm leading-relaxed">{sel.message}</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [f, setF] = useState({});
  useEffect(()=>{ setF(settings); },[settings]);
  const save = async () => { await api.put("/settings", f); await refresh(); alert("Saved"); };
  return (
    <div className="p-10" data-testid="admin-settings">
      <div className="overline text-pipeco-green">Admin</div>
      <h1 className="headline text-4xl mt-2">Settings</h1>
      <div className="mt-6 bg-white border border-black/5 p-6 max-w-xl">
        {[["whatsapp_number","WhatsApp Number"],["email","Email"],["phone","Phone"]].map(([k,l])=>(
          <div key={k} className="mb-4"><label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{l}</label>
            <input value={f[k]||""} onChange={e=>setF({...f,[k]:e.target.value})} className="w-full border border-black/10 px-3 py-2 text-sm" data-testid={`setting-${k}`}/></div>
        ))}
        <button onClick={save} className="btn-primary" data-testid="setting-save"><Save size={14}/>Save</button>
      </div>
    </div>
  );
}

export function AdminLegal() { const [pages,setPages]=useState({terms:{title:"Terms & Conditions",content:"",status:"draft"},privacy:{title:"Privacy Policy",content:"",status:"draft"}}); useEffect(()=>{api.get("/admin/legal").then(r=>setPages(r.data));},[]); const save=async()=>{await api.put("/admin/legal",pages);alert("Saved");}; return <div className="p-10" data-testid="admin-legal"><div className="overline text-pipeco-green">Super Admin</div><h1 className="headline text-4xl mt-2">Legal Pages</h1>{["terms","privacy"].map(k=><div key={k} className="mt-6 bg-white border border-black/5 p-6 max-w-3xl"><input value={pages[k].title} onChange={e=>setPages({...pages,[k]:{...pages[k],title:e.target.value}})} className="w-full border p-3 text-lg"/><textarea rows={8} value={pages[k].content} onChange={e=>setPages({...pages,[k]:{...pages[k],content:e.target.value}})} className="w-full border p-3 mt-3"/><select value={pages[k].status} onChange={e=>setPages({...pages,[k]:{...pages[k],status:e.target.value}})} className="border p-2 mt-3"><option value="draft">Draft</option><option value="published">Published</option></select></div>)}<button onClick={save} className="btn-primary mt-6"><Save size={14}/>Save Legal Pages</button></div>; }

export function AdminAccount() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({ email: "", name: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { if (user) setProfile({ email: user.email || "", name: user.name || "" }); }, [user]);
  const saveProfile = async () => {
    setError(""); setMessage("");
    try { const r = await api.put("/auth/account", profile); updateUser(r.data); setMessage("Account details saved."); }
    catch (e) { setError(String(e.response?.data?.detail || e.message)); }
  };
  const changePassword = async () => {
    setError(""); setMessage("");
    try { await api.post("/auth/account/password", passwords); setPasswords({ current_password: "", new_password: "", confirm_password: "" }); setMessage("Password updated."); }
    catch (e) { setError(String(e.response?.data?.detail || e.message)); }
  };
  return <div className="p-10" data-testid="admin-account">
    <div className="overline text-pipeco-green">Account / Security</div><h1 className="headline text-4xl mt-2">My Account</h1>
    <div className="mt-6 grid xl:grid-cols-2 gap-6 max-w-5xl">
      <div className="bg-white border border-black/5 p-6"><div className="font-display text-2xl">Account Details</div>
        <div className="mt-5 space-y-4"><input data-testid="account-name" value={profile.name} onChange={e=>setProfile({...profile, name:e.target.value})} placeholder="Name" className="w-full border border-black/10 px-4 py-3 text-sm"/><input data-testid="account-email" type="email" value={profile.email} onChange={e=>setProfile({...profile, email:e.target.value})} placeholder="Email" className="w-full border border-black/10 px-4 py-3 text-sm"/></div>
        <button data-testid="account-save" onClick={saveProfile} className="btn-primary mt-5"><Save size={14}/>Save Account</button>
      </div>
      <div className="bg-white border border-black/5 p-6"><div className="font-display text-2xl">Change Password</div>
        <div className="mt-5 space-y-3"><PasswordField testid="current-password" required value={passwords.current_password} onChange={e=>setPasswords({...passwords,current_password:e.target.value})} placeholder="Current Password"/><PasswordField testid="new-password" required value={passwords.new_password} onChange={e=>setPasswords({...passwords,new_password:e.target.value})} placeholder="New Password"/><PasswordField testid="confirm-password" required value={passwords.confirm_password} onChange={e=>setPasswords({...passwords,confirm_password:e.target.value})} placeholder="Confirm New Password"/></div>
        <button data-testid="account-password-save" onClick={changePassword} className="btn-primary mt-5"><ShieldCheck size={14}/>Update Password</button>
      </div>
    </div>
    {error && <div data-testid="account-error" className="text-red-600 text-sm mt-5">{error}</div>}{message && <div data-testid="account-success" className="text-emerald-700 text-sm mt-5">{message}</div>}
  </div>;
}

export function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin", permissions: [], is_active: true });
  const [error, setError] = useState("");
  const load = () => api.get("/admin-users").then(r=>setUsers(r.data)).catch(e=>setError(String(e.response?.data?.detail || e.message)));
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm({ email: "", name: "", password: "", role: "admin", permissions: [], is_active: true }); setEditing("new"); setError(""); };
  const openEdit = (adminUser) => { setForm({ ...adminUser, password: "", permissions: adminUser.permissions || [] }); setEditing(adminUser); setError(""); };
  const togglePermission = (permission) => setForm({...form, permissions: form.permissions.includes(permission) ? form.permissions.filter(p=>p!==permission) : [...form.permissions, permission]});
  const save = async () => {
    setError("");
    try {
      if (editing === "new") await api.post("/admin-users", form);
      else await api.put(`/admin-users/${editing.id}`, form);
      setEditing(null); load();
    } catch (e) { setError(String(e.response?.data?.detail || e.message)); }
  };
  return <div className="p-10" data-testid="admin-users">
    <div className="flex items-center justify-between"><div><div className="overline text-pipeco-green">Super Admin</div><h1 className="headline text-4xl mt-2">Admin Management</h1></div><button data-testid="admin-user-new" onClick={openNew} className="btn-primary"><Plus size={14}/>New Admin</button></div>
    {error && <div data-testid="admin-user-error" className="text-red-600 text-sm mt-4">{error}</div>}
    <div className="mt-6 bg-white border border-black/5"><table className="w-full text-sm"><thead className="bg-slate-100"><tr>{["Name","Email","Role","Status",""].map(label=><th key={label} className="text-left px-4 py-3 uppercase text-[10px] tracking-widest text-slate-500">{label}</th>)}</tr></thead><tbody>{users.map(adminUser=><tr key={adminUser.id} className="border-t border-black/5"><td className="px-4 py-3">{adminUser.name || "—"}</td><td className="px-4 py-3">{adminUser.email}</td><td className="px-4 py-3">{adminUser.role === "super_admin" ? "Super Admin" : "Admin"}</td><td className="px-4 py-3">{adminUser.is_active ? "Active" : "Disabled"}</td><td className="px-4 py-2 text-right">{adminUser.id !== user.id && <button data-testid={`admin-user-edit-${adminUser.id}`} onClick={()=>openEdit(adminUser)} className="p-2 hover:bg-slate-100"><Edit3 size={14}/></button>}</td></tr>)}</tbody></table></div>
    {editing && <div data-testid="admin-user-modal" className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={()=>setEditing(null)}><div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={e=>e.stopPropagation()}>
      <div className="flex justify-between items-center"><div className="font-display text-2xl">{editing === "new" ? "New Admin" : "Edit Admin"}</div><button data-testid="admin-user-modal-close" onClick={()=>setEditing(null)} className="p-2"><X size={18}/></button></div>
      <div className="grid md:grid-cols-2 gap-4 mt-5"><input data-testid="admin-user-name" value={form.name || ""} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Name" className="border border-black/10 px-4 py-3 text-sm"/><input data-testid="admin-user-email" type="email" value={form.email || ""} onChange={e=>setForm({...form,email:e.target.value})} placeholder="Email" className="border border-black/10 px-4 py-3 text-sm"/><div className="md:col-span-2"><PasswordField testid="admin-user-password" required={editing === "new"} value={form.password || ""} onChange={e=>setForm({...form,password:e.target.value})} placeholder={editing === "new" ? "Password (minimum 8 characters)" : "New Password (leave blank to keep current)"}/></div><select data-testid="admin-user-role" value={form.role} onChange={e=>setForm({...form,role:e.target.value})} className="border border-black/10 px-4 py-3 text-sm"><option value="admin">Admin</option><option value="super_admin">Super Admin</option></select><select data-testid="admin-user-status" value={form.is_active ? "active" : "disabled"} onChange={e=>setForm({...form,is_active:e.target.value === "active"})} className="border border-black/10 px-4 py-3 text-sm"><option value="active">Active</option><option value="disabled">Disabled</option></select></div>
      {form.role === "admin" && <div className="mt-6"><div className="overline text-slate-500">Module Access</div><div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">{ADMIN_MODULES.map(permission=><label key={permission} className="flex items-center gap-2 text-sm"><input data-testid={`admin-user-permission-${permission}`} type="checkbox" checked={form.permissions.includes(permission)} onChange={()=>togglePermission(permission)}/>{permission}</label>)}</div></div>}
      <div className="flex justify-end gap-2 mt-7"><button data-testid="admin-user-cancel" onClick={()=>setEditing(null)} className="btn-outline-dark">Cancel</button><button data-testid="admin-user-save" onClick={save} className="btn-primary"><Save size={14}/>Save Admin</button></div>
    </div></div>}
  </div>;
}
